import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
// TODO: Add react-helmet-async when React 19 support is available for SEO meta tags
import { login, getCurrentUser } from "../auth/auth";
import { jwtDecode } from "jwt-decode";
import { toast } from 'react-toastify';

import { useAuth } from "../contexts/AuthContext";
import StoreBanner from "../components/store-banner";
import OrgLoginContextModal from "../components/OrgLoginContextModal";
import "./LoginPage.css";
import "../pages/header.css";
import danceLocaleIcon from "../assets/dance_locale_3-200h.png";
import { useMobileMenu } from "../hooks/useMobileMenu";

import logoImage from "../assets/dance_locale_3-200h.png";
import foxImage from "../assets/Fox Face and Paws.png";
import { authApi } from "../api/auth";

function LoginPage() {
    const { login: setUserInContext, user, logout } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isConnectingFlow, setIsConnectingFlow] = useState(false);
    const [organisationId, setOrganisationId] = useState(null);
    const [assignedOrgs, setAssignedOrgs] = useState(null);
    const [isSelectingContext, setIsSelectingContext] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    // Self-heal: a dev ticket/booking purchase-session handoff can leave the
    // 'dance-locale-dev-mode' flag stuck in localStorage, which then routes ALL
    // API calls (including this login) to the dev database on production —
    // producing a misleading "User not found". Nobody logs in as part of a dev
    // purchase flow, so if we're on a real host (not localhost), clear it.
    useEffect(() => {
        if (window.location.hostname !== "localhost") {
            localStorage.removeItem("dance-locale-dev-mode");
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const emailParam = params.get("email");
        const organisationIdParam = params.get("orgId"); // URL param name stays as 'orgId'

        // Check if this is a connection flow (from mobile app)
        if (emailParam && organisationIdParam) {
            setIsConnectingFlow(true);
            setOrganisationId(organisationIdParam);
        }

        if (emailParam) {
            setEmail(emailParam);
            // Focus password field when email is pre-populated from mobile app
            setTimeout(() => {
                document.getElementById("password-field")?.focus();
            }, 100);
        }

        // Check if user was logged out due to token expiration
        const sessionExpired = localStorage.getItem("sessionExpired");
        if (sessionExpired) {
            setError("Your session expired. Please log in again.");
            localStorage.removeItem("sessionExpired");
        }
    }, [location]);

    useMobileMenu();

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    // // Redirect if already authenticated
    // useEffect(() => {
    //     if (isAuthenticated) {
    //         navigate("/dashboard");
    //     }
    // }, [isAuthenticated, navigate]);

    const determineUserRoute = (userData, connectingFlow, organisationId) => {
        const { accountDetails } = userData;
        
        // Check for return URL parameter first (takes priority over role-based redirects)
        const params = new URLSearchParams(location.search);
        const returnUrl = params.get("return");
        if (returnUrl) {
            try {
                const decodedUrl = decodeURIComponent(returnUrl);
                // Security check: only allow internal routes
                if (decodedUrl.startsWith("/")) {
                    return { path: decodedUrl };
                }
            } catch (error) {
                console.error("Error decoding return URL:", error);
            }
        }

        // DEPRECATED — MVP (2026-03): Facebook reviewer demo route disabled. Re-enable when Facebook integration is restored.
        // if ((userData.organisationId && userData.email === "FBReview@dom.com") || userData.email === "FBRScreenRecord@dom.com") {
        //     return { path: "/test-dashboard" };
        // }
        // Priority 1: Dance School (has organisationId)
        if (userData.organisationId) {
            if (connectingFlow && organisationId) {
                return { path: "/dashboard", state: { connectFacebook: true, organisationId: organisationId } };
            }
            return { path: "/dashboard" };
        }

        // Priority 2: Future user types (when you add them)
        // if (accountDetails?.isDanceTeacher) {
        //   return { path: "/teacher-dashboard" };
        // }

        // Priority 4: Default users (fallback)
        return { path: "/uploads" };
    };

    const handleLogin = async e => {
        e.preventDefault();
        setIsLoggingIn(true);
        setError("");

        try {
            const result = await login(email, password);

            if (result.success) {
                localStorage.setItem("accessToken", result.accessToken);
                localStorage.setItem("refreshToken", result.refreshToken);

                setUserInContext({ ...result.user, accountDetails: result.accountDetails });

                // Check if user is an assigned org admin (non-owner) — show context selection modal
                if (result.assignedOrgs && result.assignedOrgs.length > 0) {
                    setAssignedOrgs(result.assignedOrgs);
                    setIsLoggingIn(false);
                    return; // Hold navigation — modal will handle it
                }

                // Get fresh user data including account details
                const userData = await getCurrentUser();

                // Check if profile is incomplete — redirect to complete-profile first
                if (!userData.accountDetails?.firstName) {
                    const isStudentDest = !userData.organisationId;
                    const destination = isStudentDest ? '/my-hub' : determineUserRoute(userData, isConnectingFlow, organisationId).path;
                    navigate('/complete-profile', { state: { destination } });
                    return;
                }

                // Check if user is a student (no org) → redirect to my-hub
                const isStudent = !userData.organisationId;
                if (isStudent) {
                    setUserInContext({ ...result.user, accountDetails: result.accountDetails });
                    navigate('/my-hub');
                    return;
                }

                const route = determineUserRoute(userData, isConnectingFlow, organisationId);

                navigate(route.path, route.state || {});
            } else {
                setError("Invalid email and/or password");
            }
        } catch (error) {
            setError("Login failed due to an unexpected error. Please try again later.");
            console.error("LoginPage: Error in handleLogin:", error);
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleSelectOrg = async (orgId) => {
        setIsSelectingContext(true);
        try {
            const data = await authApi.selectLoginContext(orgId);
            if (data.accessToken) {
                localStorage.setItem("accessToken", data.accessToken);
                const newUserData = await getCurrentUser();
                setUserInContext(newUserData);
                setAssignedOrgs(null);
                // Role-aware redirect for orgContext users:
                // helpers (viewDashboard: false) → /classes, admins/owners → /dashboard
                if (newUserData.loginType === "orgContext") {
                    const capabilities = newUserData.permissions?.capabilities;
                    if (capabilities?.viewDashboard) {
                        navigate("/dashboard");
                    } else {
                        navigate("/classes");
                    }
                } else {
                    navigate("/dashboard");
                }
            } else {
                toast.error("Failed to sign in to this account. Please try again.");
            }
        } catch (err) {
            toast.error("Failed to sign in to this account. Please try again.");
            console.error("select-login-context error:", err);
        } finally {
            setIsSelectingContext(false);
        }
    };

    const handleSelectPersonal = async () => {
        setAssignedOrgs(null);
        const userData = await getCurrentUser();
        if (!userData.accountDetails?.firstName) {
            const isStudentDest = !userData.organisationId;
            const destination = isStudentDest ? '/my-hub' : determineUserRoute(userData, isConnectingFlow, organisationId).path;
            navigate('/complete-profile', { state: { destination } });
            return;
        }
        const isStudent = !userData.organisationId;
        if (isStudent) {
            navigate('/my-hub');
            return;
        }
        const route = determineUserRoute(userData, isConnectingFlow, organisationId);
        navigate(route.path, route.state || {});
    };

    return (
        <div className="login-container10">
            {/* TODO: Add SEO meta tags when react-helmet-async supports React 19
                Title: "Login - Dance Locale" */}

            {/* Use the exact same header structure as home page */}
            <div data-role="Header" className="home-header-container">
                <header className="home-header">
                    <a href="/" className="home-logo1">
                        <img alt="logo" src={logoImage} className="home-image1" />
                        <span className="nav-wordmark">Dance Localé</span>
                    </a>
                    <div className="home-menu1">
                        <a href="/" className="home-text10" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Home
                        </a>
                        <span className="home-text12" style={{ color: "#ff5f25" }}>
                            Login
                        </span>
                        <a
                            href="/contact"
                            className="home-text13"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Contact
                        </a>
                    </div>
                    <div className="home-container11">
                        <StoreBanner />
                    </div>
                    <div data-role="BurgerMenu" className="home-burger-menu">
                        <svg viewBox="0 0 1024 1024" className="home-menu2">
                            <path d="M810.667 725.333h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 426.667h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 128h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                        </svg>
                    </div>
                </header>

                {/* Include the mobile menu structure */}
                <div data-role="MobileMenu" className="home-mobile-menu">
                    <div className="home-top">
                        <div className="home-logo2">
                            <img alt="logo" src={danceLocaleIcon} className="home-image2" />
                        </div>
                        <div data-role="CloseMobileMenu" className="home-close-menu">
                            <svg viewBox="0 0 1024 1024" className="home-icon15">
                                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className="home-mid">
                        <a href="/" className="home-text14" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Home
                        </a>
                        {user ? (
                            <>
                                {user.organisationId ? (
                                    <a
                                        href="/dashboard"
                                        className="home-text16"
                                        style={{ cursor: "pointer", textDecoration: "none" }}>
                                        Dashboard
                                    </a>
                                ) : (
                                    <a
                                        href="/events"
                                        className="home-text16"
                                        style={{ cursor: "pointer", textDecoration: "none" }}>
                                        Events
                                    </a>
                                )}
                                <span className="home-text17" onClick={handleLogout} style={{ cursor: "pointer" }}>
                                    Logout
                                </span>
                            </>
                        ) : (
                            <>
                                <span
                                    className="home-text16"
                                    onClick={() => navigate("/login")}
                                    style={{ cursor: "pointer", color: "#ff5f25" }}>
                                    Login
                                </span>
                                <a
                                    href="/contact"
                                    className="home-text17"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Contact
                                </a>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Login form content */}
            <div className="login-main">
                <div className="login-form-container">
                    <img src={foxImage} alt="Fox Face and Paws" className="login-fox-overlay" />
                    <div className="login-form-card">
                        <div className="login-header">
                            {/* <img src={logoImage} alt="Dance Localé Logo" className="login-logo" /> */}
                            <h1 className="login-title">Welcome Back</h1>
                            <p className="login-subtitle">Sign in to your Dance Localé account</p>
                        </div>

                        {error && <div className="login-error">{error}</div>}

                        <form onSubmit={handleLogin} className="login-form">
                            <div className="login-field">
                                <label className="login-label" htmlFor="login-email">Email</label>
                                <input
                                    type="email"
                                    id="login-email"
                                    name="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className="login-input"
                                    placeholder="Enter your email"
                                    required
                                />
                            </div>
                            <div className="login-field">
                                <label className="login-label" htmlFor="password-field">Password</label>
                                <input
                                    id="password-field"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    className="login-input"
                                    placeholder="Enter your password"
                                    required
                                />
                            </div>
                            <button type="submit" disabled={isLoggingIn} className="login-button">
                                {isLoggingIn ? "Signing in..." : "Sign In"}
                            </button>
                            <div className="login-forgot-password">
                                <a
                                    href="/forgot-password"
                                    className="login-forgot-link"
                                    style={{ textDecoration: "none" }}>
                                    Forgot Password?
                                </a>
                            </div>
                            <p className="login-registration-link">
                                Don't have an account?{" "}
                                <span
                                    className="login-register-link"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate("/register")}
                                    onKeyDown={e => (e.key === "Enter" || e.key === " ") && navigate("/register")}>
                                    Register here
                                </span>
                            </p>
                        </form>
                    </div>
                </div>
            </div>
            {assignedOrgs && assignedOrgs.length > 0 && (
                <OrgLoginContextModal
                    orgs={assignedOrgs}
                    onSelectOrg={handleSelectOrg}
                    onSelectPersonal={handleSelectPersonal}
                    isLoading={isSelectingContext}
                />
            )}
        </div>
    );
}

export default LoginPage;
